import { useEffect, useRef, useState } from 'react';
import { t } from 'i18next';
import { GUID } from '../graphql/GetUserProfileInfo';
import { useAuth } from '../hooks/authContext';
import { UserExpenseSummaryType } from '../graphql/GetUserExpenseSummary';
import useUserExpenseForApprovalSummary from '../hooks/useUserExpensesForApprovalSummary';
import useUserExpenseForApprovalList from '../hooks/useUserExpensesForApprovalList';
import { ExpenseStatusesType } from '../graphql/GetExpenseStatuses';
import useExpenseStatuses from '../hooks/useExpenseStatuses';
import { Option } from '../utils/Interfaces';
import FosAddExpenseActionSheet from '../atom/FosAddExpenseActionSheet';
import { UserExpenseObject } from '../graphql/GetUserExpenseForApprovalList';

enum ExpenseMethod {
  MILEAGE = 3,
  OUT_OF_POCKET = 2,
  CORPORATE_CARD = 1,
}

const UserExpensesForApprovalViewModel = () => {
  const { userState } = useAuth()!;
  const expenseForApprovalFilterModal = useRef<HTMLIonModalElement>(null);
  const [userExpenseSummaryForApprovalFiltered, setUserExpenseSummaryForApprovalFiltered] = useState<UserExpenseSummaryType[]>([]);
  const [userExpenseListForApprovalFiltered, setUserExpenseListForApprovalFiltered] = useState<UserExpenseObject[]>([]);
  const [expenseStatusList, setExpenseStatusList] = useState<ExpenseStatusesType[]>([]);
  const [searchTerm, setSearchTerm] = useState(localStorage.getItem('userExpensesForApprovalSearchTerm') ?? ''); 
  const [expenseMethodId, setExpenseMethodId] = useState<number>(localStorage.getItem('userExpensesForApprovalCategorySelected') ? Number(localStorage.getItem('userExpensesForApprovalCategorySelected')) : 0);
  const [filterByStatus, setFilterByStatus] = useState<string>(localStorage.getItem('userExpensesForApprovalStatusId') ?? '2');
  const [expenseStatusFilter, setExpenseStatusFilter] = useState<number | null>(localStorage.getItem('userExpensesForApprovalStatusId') ? Number(localStorage.getItem('userExpensesForApprovalStatusId')) : null);
  const { handleClick } = FosAddExpenseActionSheet();
  const regionId = userState.userAuthPayload?.regionId!;
  const userGuid = window.localStorage.getItem(GUID) as string;

  const { expenseStatuses } = useExpenseStatuses(userState);
  
  const defaultStatus = 2;

  useEffect(() => {
    if (expenseStatuses) {
      setExpenseStatusList(expenseStatuses);
    }
  }, [expenseStatuses]);

  const { userExpenseForApprovalSummary, isFetching, refetch } = useUserExpenseForApprovalSummary(userState, regionId, userGuid);

  const statusId = expenseStatusFilter || defaultStatus;
  const { userExpenseForApprovalList, isFetching: isFetchingExpenses, refetch: refetchExpenseList } = useUserExpenseForApprovalList(userState, regionId, userGuid, expenseMethodId, statusId);

  const onSearchChange = (term: string) => {
    setSearchTerm(term);
    localStorage.setItem('userExpensesForApprovalSearchTerm', term);
  };

  useEffect(() => {
    if (searchTerm) {
      const filteredItems: UserExpenseObject[] = userExpenseForApprovalList?.filter(userExpense => 
        userExpense?.expense?.comments?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        userExpense?.expense?.merchant?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        userExpense?.employeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        userExpense?.expense?.id === Number(searchTerm)) || [];
      setUserExpenseListForApprovalFiltered(filteredItems);
    } else {
      setUserExpenseSummaryForApprovalFiltered(userExpenseForApprovalSummary || []);
      setUserExpenseListForApprovalFiltered(userExpenseForApprovalList || []);
    }

  }, [expenseMethodId, searchTerm, userExpenseForApprovalList, userExpenseForApprovalSummary]);

  const addExpense = () => handleClick();
  const navigateToUserExpenseCategory = (id: number) => {
    localStorage.setItem('userExpensesForApprovalCategorySelected', id.toString());
    setExpenseMethodId(id);
    setSearchTerm('');
    localStorage.setItem('userExpensesForApprovalSearchTerm', '');
  };

  const selectedExpenseMethodName =  t(`${ExpenseMethod[expenseMethodId]}`);

  const backToCategories = () => {
    localStorage.setItem('userExpensesForApprovalCategorySelected', '0');
    setExpenseMethodId(0);
    setSearchTerm('');
    localStorage.setItem('userExpensesForApprovalSearchTerm', '');
    refetch();
  };

  const showResults = () => {
    // do filter
    const filterFromStorage = localStorage.getItem('userExpensesForApprovalStatusId');

    setExpenseStatusFilter(filterByStatus ? Number(filterFromStorage ?? filterByStatus) : null);
    expenseForApprovalFilterModal.current?.dismiss();
  };

  const clearFilter = () => {
    setFilterByStatus(defaultStatus.toString());
    localStorage.setItem('userExpensesForApprovalStatusId', defaultStatus.toString());
    setExpenseStatusFilter(null);
    expenseForApprovalFilterModal.current?.dismiss();
  };

  const expenseStatusListOptions: Option[] = expenseStatusList
    .map((expenseStatus: ExpenseStatusesType) => ({ label: expenseStatus.name, value: expenseStatus.id.toString() }));

  return { 
    userExpenseSummaryForApprovalFiltered,
    userExpenseListForApprovalFiltered,
    isFetching,
    isFetchingExpenses,
    expenseMethodId,
    selectedExpenseMethodName,
    searchTerm,
    expenseForApprovalFilterModal,
    expenseStatusListOptions,
    filterByStatus,
    clearFilter,
    showResults,
    setFilterByStatus,
    onSearchChange,
    addExpense,
    navigateToUserExpenseCategory,
    backToCategories,
    refetchExpenseList,
  };
};

export default UserExpensesForApprovalViewModel;